/**
 * preview-force.ts — preview-ONLY force-ON allowlist for server-side PostHog
 * feature flags. Sibling in shape and in safety posture to `src/lib/faults.ts`
 * (read that file first: this one deliberately copies its production-gate-first
 * construction).
 *
 * WHY THIS EXISTS
 * ---------------
 * Every screening surface is gated `if (!(await getServerFeatureFlag(
 * "screening-api", false))) notFound()`. The flag ships OFF (dark ladder), so
 * the employers-zone e2e wizard walkthrough
 * (tests/e2e/employers-zone/screening-wizard-walkthrough.spec.ts) took its
 * flag-off 404 branch, returned after two navigations, and reported GREEN
 * having exercised none of the wizard. A spec that cannot reach the product it
 * names is not coverage — it is a green check with a hole behind it.
 *
 * PostHog-side targeting cannot fix this: `getServerFeatureFlag` sends a
 * CONSTANT `distinct_id` ("server-global"), so PostHog cannot tell a preview
 * worker apart from production. And the ephemeral preview worker is issued
 * `NEXT_PUBLIC_POSTHOG_KEY: ""` by `scripts/gen-preview-wrangler.ts`
 * (cred isolation) — it never calls `/decide` at all, it just returns the
 * caller's fallback. The only place the decision can be made is the preview
 * worker's own env.
 *
 * INERT IN PRODUCTION BY CONSTRUCTION — TWO independent gates, both of which
 * must pass, and NEITHER of which production can satisfy:
 *
 *   1. `ENVIRONMENT === "production"` returns immediately (the faults.ts law,
 *      kept verbatim so the two files read the same and neither can be
 *      "fixed" into the other's shape by accident).
 *   2. `ENVIRONMENT` must then ALSO be one of an explicit, closed set of
 *      known non-production values. This is stricter than faults.ts on
 *      purpose: `ENVIRONMENT` is set in the apex `wrangler.jsonc` but is
 *      ABSENT from every zone worker's `vars` (apps/employers, apps/reports,
 *      …), so a bare `!== "production"` test would read *undefined* as
 *      "not production" and fail OPEN on exactly the zone this mechanism was
 *      built for. Unknown/absent/misspelled ⇒ no force. Fails CLOSED.
 *
 * Do NOT reorder these checks and do NOT relax check 2 into a negation.
 *
 * NOT A FLAG SHADOW (root CLAUDE.md, "Forbidden Actions"): this can only ever
 * force a flag ON, never OFF, and only outside production. In production the
 * function is a constant `false` for every input, so PostHog remains the sole
 * switch there — which is what that rule protects. The allowlist is a runtime
 * env var, never a committed constant, so nothing in the source tree names a
 * flag it turns on.
 *
 * `env` is intentionally `unknown` for the same reason faults.ts's is: call
 * sites pass whatever env object they already hold (`process.env` on the
 * OpenNext worker, a `CloudflareEnv`), both of which are plain string-keyed
 * objects.
 */

import { isKnownNonProductionEnvironment } from "@/lib/env-gate";

interface PreviewForceEnvShape {
	ENVIRONMENT?: string;
	PREVIEW_FORCE_FLAGS?: string;
	/** Manifest-declared-on flags (`e2e_state: "on"`), emitted by
	 *  scripts/gen-preview-wrangler.ts. Unioned with PREVIEW_FORCE_FLAGS, which
	 *  a lane may overwrite per pass. */
	PREVIEW_FORCE_FLAGS_BASE?: string;
}

/**
 * Per-request force for {@link previewForcedVariant}: a cookie holding
 * comma-separated `flagKey=variant` tokens. Honoured outside production only.
 */
export const PREVIEW_FORCE_COOKIE = "ld_preview_force";

function isPreviewForceEnvShape(env: unknown): env is PreviewForceEnvShape {
	return typeof env === "object" && env !== null;
}

/**
 * Comma-separated allowlist parsing, same convention as
 * `parseFaultAllowlist` in src/lib/faults.ts.
 */
function parseForcedFlags(raw: string | undefined): Set<string> {
	if (!raw) return new Set();
	return new Set(
		raw
			.split(",")
			.map((f) => f.trim())
			.filter(Boolean),
	);
}

/**
 * `true` only when `env.PREVIEW_FORCE_FLAGS` names `flagKey` AND
 * `env.ENVIRONMENT` is an explicitly-known non-production value.
 *
 * `false` means "no opinion" — the caller falls through to the real PostHog
 * read. It never means "off", so this can never mask a flag PostHog says is on.
 */
export function previewForcedFlag(env: unknown, flagKey: string): boolean {
	if (!isPreviewForceEnvShape(env)) return false;

	// ENVIRONMENT GATE FIRST. Must stay first: this is the whole safety
	// guarantee. Never move the PREVIEW_FORCE_FLAGS read above this line.
	//
	// Positive test, not `!== "production"`: absent / unrecognized ENVIRONMENT
	// fails CLOSED. Zone workers ship no ENVIRONMENT var at all, so "not
	// production" is not enough evidence to force anything on. The closed set
	// lives in src/lib/env-gate.ts, shared with src/lib/faults.ts so the two
	// gates cannot drift apart.
	if (!isKnownNonProductionEnvironment(env.ENVIRONMENT)) return false;

	return (
		parseForcedFlags(env.PREVIEW_FORCE_FLAGS).has(flagKey) ||
		parseForcedFlags(env.PREVIEW_FORCE_FLAGS_BASE).has(flagKey)
	);
}

/**
 * Multivariate sibling of {@link previewForcedFlag}: a `flagKey=variant` token
 * in `PREVIEW_FORCE_FLAGS` (e.g. `gig-placement=mixed`) returns `"mixed"`.
 * Same two production gates, same "no opinion" semantics: `null` means fall
 * through to the real PostHog read. A bare `flagKey` token carries no variant
 * and returns `null` (a boolean force cannot pick an arm).
 *
 * Only an e2e run that sets the token explicitly gets a variant — the
 * manifest-rendered list (`e2e_state: "on"`) never emits `key=value` tokens.
 *
 * `requestTokens` is the raw value of the {@link PREVIEW_FORCE_COOKIE} request
 * cookie, same `key=variant` syntax. It lets ONE e2e lane drive every arm of
 * an experiment per test, instead of one arm per worker-wide lane (2026-10-27:
 * no lane ever ran the apply-cap-wall test arm, so its 800 ms flag timeout
 * shipped and dropped half the test arm to control). It is read only after
 * the same ENVIRONMENT gate, so production ignores the cookie entirely.
 */
export function previewForcedVariant(
	env: unknown,
	flagKey: string,
	requestTokens?: string | null,
): string | null {
	if (!isPreviewForceEnvShape(env)) return null;
	// ENVIRONMENT GATE FIRST — see previewForcedFlag.
	if (!isKnownNonProductionEnvironment(env.ENVIRONMENT)) return null;
	for (const token of [
		...parseForcedFlags(requestTokens ?? undefined),
		...parseForcedFlags(env.PREVIEW_FORCE_FLAGS),
	]) {
		const sep = token.indexOf("=");
		if (sep <= 0) continue;
		if (token.slice(0, sep).trim() !== flagKey) continue;
		const variant = token.slice(sep + 1).trim();
		return variant.length > 0 ? variant : null;
	}
	return null;
}
